import React, { useState, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Upload, FileSpreadsheet, Download, CheckCircle, AlertCircle, Sparkles,
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';
import UploadValidation from '@/components/UploadValidation';

const ImportStudio = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [bomName, setBomName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);
      const fileName = uploadedFile.name.replace(/\.[^/.]+$/, '');
      if (!bomName) setBomName(fileName);

      const errors = [];
      const warnings = [];
      if (uploadedFile.size > 5 * 1024 * 1024) warnings.push('File is larger than 5MB — processing may take longer');
      if (!uploadedFile.name.match(/\.(csv|xlsx|xls|pdf)$/i)) {
        errors.push('Unsupported file format. Please upload CSV, XLS, XLSX, or PDF');
      }
      if (uploadedFile.name.match(/\.pdf$/i)) {
        warnings.push('PDF detected — LLM-powered extraction will be used. Accuracy may vary.');
      }
      setValidationErrors(errors);
      setValidationWarnings(warnings);
    }
  }, [bomName]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (bomName) formData.append('bom_name', bomName);

      const token = localStorage.getItem('session_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bom/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || 'Upload failed');
      }

      const data = await res.json();
      if (data.warnings?.length > 0) data.warnings.forEach(w => toast.warning(w));
      toast.success(data.message || `BOM uploaded with ${data.total_components} components`);
      navigate('/bom');
    } catch (err) {
      toast.error(err.message || 'Failed to upload BOM');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Layout title="Upload BOM" subtitle="Upload your Bill of Materials for AI-powered analysis">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="import-studio-page">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-gray-900 flex items-center gap-2">
              <Upload className="h-5 w-5 text-green-600" />
              Upload Your BOM
            </CardTitle>
            <CardDescription>
              Drag and drop your BOM file or click to browse. Optimization parameters are configured separately after upload.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Drop Zone */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all
                ${isDragActive ? 'border-green-500 bg-green-50' : file ? 'border-green-400 bg-green-50/50' : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'}`}
            >
              <input {...getInputProps()} data-testid="file-input" />
              {file ? (
                <div className="flex items-center justify-center gap-4">
                  <FileSpreadsheet className="h-12 w-12 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium text-green-900">{file.name}</p>
                    <p className="text-sm text-green-700">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Badge className="bg-green-600 text-white">Ready</Badge>
                </div>
              ) : (
                <>
                  <FileSpreadsheet className={`h-14 w-14 mx-auto mb-3 ${isDragActive ? 'text-green-500' : 'text-gray-400'}`} />
                  <p className="text-base font-medium text-gray-700 mb-1">
                    {isDragActive ? 'Drop your file here' : 'Drag & drop your BOM file'}
                  </p>
                  <p className="text-sm text-gray-500 mb-3">or click to browse</p>
                  <Button variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">Browse Files</Button>
                  <p className="text-xs text-gray-400 mt-3">Supported: XLSX, XLS, CSV, PDF | Max: 10MB</p>
                </>
              )}
            </div>

            {/* Validation */}
            {file && (validationErrors.length > 0 || validationWarnings.length > 0) && (
              <UploadValidation errors={validationErrors} warnings={validationWarnings} canContinue={validationErrors.length === 0} />
            )}

            {/* BOM Name */}
            <div>
              <Label htmlFor="bomNameInput">BOM Name <span className="text-xs text-gray-400">(auto-fills from filename)</span></Label>
              <Input
                id="bomNameInput"
                value={bomName}
                onChange={e => setBomName(e.target.value)}
                placeholder="e.g., Main Controller Board v2.1"
                className="mt-1"
                data-testid="bom-name-upload-input"
              />
            </div>

            {/* Template Download */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Download className="h-4 w-4 text-blue-600" />
                  <div>
                    <p className="font-medium text-sm text-blue-900">Need a template?</p>
                    <p className="text-xs text-blue-700">Download our Excel template for best results</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <a href="/sample_bom.xlsx" download>
                    <Button variant="outline" size="sm" className="border-blue-400 text-blue-600 text-xs">Template (XLSX)</Button>
                  </a>
                  <a href="/sample_bom_iot.xlsx" download>
                    <Button variant="outline" size="sm" className="border-blue-400 text-blue-600 text-xs">Sample IoT BOM</Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Upload Actions */}
            <div className="flex justify-between items-center pt-2">
              <Button variant="outline" onClick={() => navigate('/bom')} data-testid="cancel-upload-btn">Cancel</Button>
              <Button
                onClick={handleUpload}
                disabled={!file || validationErrors.length > 0 || uploading}
                className="bg-green-600 hover:bg-green-700 text-white px-6"
                data-testid="start-upload-btn"
              >
                {uploading ? (
                  <><span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full inline-block" /> Uploading...</>
                ) : (
                  <><Sparkles className="h-4 w-4 mr-2" /> Upload & Analyze</>
                )}
              </Button>
            </div>

            {/* Info */}
            <p className="text-xs text-gray-400 text-center">
              After upload, your BOM will be processed automatically. You can configure optimization parameters when running an optimization.
            </p>
          </CardContent>
        </Card>

        {uploading && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="py-10 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center animate-pulse">
                <Sparkles className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Analyzing Your BOM</h3>
              <p className="text-sm text-gray-600 mb-4">Processing {file?.name}...</p>
              <div className="max-w-xs mx-auto space-y-2">
                {['Parsing BOM structure', 'Matching components', 'Finding alternates', 'Calculating savings', 'Assessing risks'].map((task, idx) => (
                  <div key={task} className="flex items-center gap-2 text-sm">
                    <CheckCircle className={`h-4 w-4 ${idx < 2 ? 'text-green-600' : 'text-gray-300'}`} />
                    <span className={idx < 2 ? 'text-gray-900' : 'text-gray-400'}>{task}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default ImportStudio;
